import { useEffect, useRef, useState } from "react";
import axios from "axios";
// import { useAuth } from "@/hooks/useAuth";
import { useParams } from "react-router-dom";
import {
  generateChampProfondIdentificationTargets,
  generateZoneFrontiereTargets,
  generateChampProfondFormesTargets,
} from "@/modules/targetsGeneration";
import { selectRandomImages } from "@/utils/selectRandomImages";
import {
  ImageSelectionParams,
  ImageType,
  GeometrieSpecificite,
  ObjetsSpecificite,
  ColorImages,
  GeometrieFormes,
  selectImagesCatalog,
} from "@/utils/selectImagesCatalog";
import ModuleRenderer from "@/modules/launcher/ModuleRenderer";
import { computeVisibleGrid } from "@/utils/computeVisibleGrid";
import { saveResultsModules } from "@/utils/saveResultsModules";

export interface SessionInfo {
  sujet: string;
  notes: string;
  visualFieldSelection: string;
  visualFieldSelectionId: string;
  visualFieldInput?: string;
  resultsZF: string | null;
  resultsOB: string | null;
  resultsOT: string | null;
  resultsPVS: string | null;
  resultsZPCCF: string | null;
  resultsZPFCF: string | null;
  resultsZPI: string | null;
  resultsZPL: string | null;
  targetEnCours_Zf_Bilan: string | null;
  initialTargetsLengthEncours_Zf_Bilan: number | null;
  status: "EN_COURS" | "PREVUE" | "REALISEE";
  parameters: {
    OB?: ModuleParameters;
    OT?: ModuleParameters;
    PVS?: ModuleParameters;
    ZF?: ZFParameters;
    ZPCCF?: ZPCCFParameters;
    ZPFCF?: ZPFCFParameters;
    ZPI?: ZPIParameters;
    ZPL?: ZPLParameters;
  };
}

export interface ModuleParameters {
  contenu?: string;
  duree?: string;
  selected: boolean;
  sous_module: string;
  vitesse?: string;
}

export interface ZFParameters {
  nombre_cible: string;
  selected: boolean;
  sous_module: string;
  zt: string;
}

interface ZPCCFParameters {
  duree: string;
  mouvement: string;
  selected: boolean;
  side_aveugle: string;
  sous_module: string;
  taille: string[];
  zt: string;
}

interface ZPFCFParameters {
  duree: string;
  mouvement: string;
  selected: boolean;
  sous_module: string;
  taille: string[];
  type_forme: string[];
  zt: string;
  couleur_cible: string;
  couleur_fond: string;
}

interface ZPIParameters {
  couleur_cible: string;
  couleur_fond: string;
  duree: string;
  mouvement: string;
  nombre_par_hemichamp: string;
  selected: boolean;
  side: string;
  sous_module: string;
  taille: string[];
  type_forme: string[];
  zt: string;
}

interface ZPLParameters {
  couleur_cible: string;
  couleur_fond: string;
  duree: string;
  mouvement: string;
  nombre_par_hemichamp: string;
  selected: boolean;
  side: string;
  sous_module: string;
  taille: string[];
  zt: string;
}

// Define the Target type
interface TargetFeedback {
  position: string;
  size: number;
  gif: string;
  blinkAndResizing: boolean;
}

// Define the TargetGroup type
export interface TargetTypeFeedback {
  sameShape: boolean;
  targets: TargetFeedback[];
}

type TargetIdentification = {
  position: string;
  size: number;
  gif: string;
};

// Define the Salve type
export type TargetTypeIdentification = {
  targets: TargetIdentification[];
};

export default function ModulePreloader({
  role,
}: {
  role: "practitioner" | "patient";
}) {
  // const { userContext } = useAuth();
  const { sessionId, patientId } = useParams();
  const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);
  const [removedColumns, setRemovedColumns] = useState<number[]>([]);
  const [removedRows, setRemovedRows] = useState<number[]>([]);
  const casesSelectedRef = useRef({
    ZP: [],
    ZF: [],
    ZV: [],
  });
  const [casesSelectedGeneral, setCasesSelectedGeneral] = useState<{
    ZP: string[];
    ZF: string[];
    ZV: string[];
  }>({ ZP: [], ZF: [], ZV: [] });
  const [casesSelectedZPL, setCasesSelectedZPL] = useState<{
    ZP: string[];
    ZF: string[];
    ZV: string[];
  }>({ ZP: [], ZF: [], ZV: [] });
  const [casesSelectedZPFCF, setCasesSelectedZPFCF] = useState<{
    ZP: string[];
    ZF: string[];
    ZV: string[];
  }>({ ZP: [], ZF: [], ZV: [] });
  const [casesSelectedZPCCF, setCasesSelectedZPCCF] = useState<{
    ZP: string[];
    ZF: string[];
    ZV: string[];
  }>({ ZP: [], ZF: [], ZV: [] });
  const [casesSelectedZPI, setCasesSelectedZPI] = useState<{
    ZP: string[];
    ZF: string[];
    ZV: string[];
  }>({ ZP: [], ZF: [], ZV: [] });
  const [selectionVisualFieldId, setSelectionVisualFieldId] = useState(null);

  useEffect(() => {
    if (sessionId) {
      axios
        .get(`/${role}/session/${sessionId}`)
        .then((response) => {
          const sessionData = response.data;
          const adaptedParameters = sessionData.parameters.reduce(
            (
              acc: { [key: string]: ModuleParameters },
              param: { content: string }
            ) => {
              const content = JSON.parse(param.content);
              const key = content.sous_module;
              acc[key] = { ...acc[key], ...content };
              return acc;
            },
            {}
          );

          setSelectionVisualFieldId(sessionData.visualFieldSelection.id);
          setSessionInfo({
            parameters: adaptedParameters,
            notes: sessionData.notes || "",
            sujet: sessionData.sujet || "",
            visualFieldSelection: sessionData.visualFieldSelection.timeline,
            visualFieldSelectionId: sessionData.visualFieldSelection.id,
            visualFieldInput: sessionData.visualFieldInput || null,
            status: sessionData.status,
            resultsZF: sessionData.resultsZF || null,
            resultsOB: sessionData.resultsOB || null,
            resultsOT: sessionData.resultsOT || null,
            resultsPVS: sessionData.resultsPVS || null,
            resultsZPCCF: sessionData.resultsZPCCF || null,
            resultsZPFCF: sessionData.resultsZPFCF || null,
            resultsZPI: sessionData.resultsZPI || null,
            resultsZPL: sessionData.resultsZPL || null,
            targetEnCours_Zf_Bilan: sessionData.targetEnCours_Zf_Bilan || null,
            initialTargetsLengthEncours_Zf_Bilan:
              sessionData.initialTargetsLengthEncours_Zf_Bilan || null,
          });
        })
        .catch((error) => {
          console.error(
            "Erreur lors de la récupération des données de la séance:",
            error
          );
        });
    }
  }, [sessionId]);

  useEffect(() => {
    if (!selectionVisualFieldId) return;
    let axiosUrlApiSelectionById;
    if (role === "practitioner") {
      axiosUrlApiSelectionById = `/${role}/visualField/${patientId}/selectionId/${selectionVisualFieldId}/selection`;
    } else {
      axiosUrlApiSelectionById = `/${role}/visualField/selectionId/${selectionVisualFieldId}/selection`;
    }

    let axiosUrlApiSelectionByTimeline;
    if (role === "practitioner") {
      axiosUrlApiSelectionByTimeline = `/${role}/visualField/${patientId}/timeline/`;
    } else {
      axiosUrlApiSelectionByTimeline = `/${role}/visualField/timeline/`;
    }

    axios
      .get(axiosUrlApiSelectionById)
      .then((response) => {
        let generalSelection;
        if (
          sessionInfo?.status === "EN_COURS" &&
          sessionInfo.visualFieldInput &&
          (sessionInfo.resultsZF ||
            sessionInfo.resultsZPFCF ||
            sessionInfo.resultsZPCCF ||
            sessionInfo.resultsZPI ||
            sessionInfo.resultsZPL)
        ) {
          generalSelection = JSON.parse(sessionInfo.visualFieldInput);
          casesSelectedRef.current = generalSelection;
        } else {
          generalSelection = JSON.parse(response.data.selection);
          casesSelectedRef.current = generalSelection;
        }

        setCasesSelectedGeneral(generalSelection);

        if (sessionInfo?.parameters.ZPFCF?.zt === "Automatique") {
          setCasesSelectedZPFCF(generalSelection);
        }
        if (sessionInfo?.parameters.ZPL?.zt === "Automatique") {
          setCasesSelectedZPL(generalSelection);
        }
        if (sessionInfo?.parameters.ZPI?.zt === "Automatique") {
          setCasesSelectedZPI(generalSelection);
        }
        if (sessionInfo?.parameters.ZPCCF?.zt === "Automatique") {
          setCasesSelectedZPCCF(generalSelection);
        }
      })
      .catch((error) => {
        console.error(
          "Erreur lors de la récupération des données de la sélection des champs visuels:",
          error
        );
      });

    if (sessionInfo?.parameters.ZPCCF?.zt !== "Automatique") {
      axios
        .get(
          axiosUrlApiSelectionByTimeline +
            sessionInfo?.parameters.ZPCCF?.zt +
            "/selection"
        )
        .then((response) => {
          setCasesSelectedZPCCF(JSON.parse(response.data.selection));
        })
        .catch((error) => {
          console.error(
            "Erreur lors de la récupération des données de la sélection des champs visuels:",
            error
          );
        });
    }
    if (sessionInfo?.parameters.ZPFCF?.zt !== "Automatique") {
      axios
        .get(
          axiosUrlApiSelectionByTimeline +
            sessionInfo?.parameters.ZPFCF?.zt +
            "/selection"
        )
        .then((response) => {
          setCasesSelectedZPFCF(JSON.parse(response.data.selection));
        })
        .catch((error) => {
          console.error(
            "Erreur lors de la récupération des données de la sélection des champs visuels:",
            error
          );
        });
    }
    if (sessionInfo?.parameters.ZPL?.zt !== "Automatique") {
      axios
        .get(
          axiosUrlApiSelectionByTimeline +
            sessionInfo?.parameters.ZPL?.zt +
            "/selection"
        )
        .then((response) => {
          setCasesSelectedZPL(JSON.parse(response.data.selection));
        })
        .catch((error) => {
          console.error(
            "Erreur lors de la récupération des données de la sélection des champs visuels:",
            error
          );
        });
    }
    if (sessionInfo?.parameters.ZPI?.zt !== "Automatique") {
      axios
        .get(
          axiosUrlApiSelectionByTimeline +
            sessionInfo?.parameters.ZPI?.zt +
            "/selection"
        )
        .then((response) => {
          setCasesSelectedZPI(JSON.parse(response.data.selection));
        })
        .catch((error) => {
          console.error(
            "Erreur lors de la récupération des données de la sélection des champs visuels:",
            error
          );
        });
    }
  }, [selectionVisualFieldId, patientId]);

  const [targetZoneFrontiere, setTargetZoneFrontiere] = useState<string[]>([]);
  const [
    targetChampsProfondIdentification,
    setTargetChampsProfondIdentification,
  ] = useState<TargetTypeIdentification[]>([]);
  const [targetChampsProfondLocalisation, setTargetChampsProfondLocalisation] =
    useState<TargetTypeIdentification[]>([]);
  const [
    targetChampsProfondCouleurFeedback,
    setTargetChampsProfondCouleurFeedback,
  ] = useState<TargetTypeFeedback[]>([]);
  const [
    targetsChampProfondFormesTargets,
    setTargetsChampProfondFormesTargets,
  ] = useState<TargetTypeFeedback[]>([]);

  useEffect(() => {
    if (!sessionInfo || !casesSelectedGeneral) return;

    const nombreParHemichamp = (
      nombre_par_hemichamp?: string
    ): [number, number] => {
      switch (nombre_par_hemichamp) {
        case "1":
          return [1, 1];
        case "3":
          return [1, 3];
        case "6":
          return [1, 6];
        default:
          return [1, 1]; // Default value
      }
    };

    const convertSizesToNumbers = (sizes: string[]): number[] => {
      return sizes.map((size) => parseInt(size, 10));
    };

    const selectFilesGifsForModulesIdentificationAndFormes = (
      couleurs: string,
      type_forme: string[],
      backgroundColor: string,
      mouvement: boolean
    ) => {
      // Déterminer le type d'image et les spécificités basées sur type_forme
      const types: ImageType[] = [];
      const geometrieSpecificite: GeometrieSpecificite[] = [];
      const objetsSpecificite: ObjetsSpecificite[] = [];
      const couleurInclude: ColorImages[] = [];
      const couleurExclude: ColorImages[] = [];
      const geometrieFormes: GeometrieFormes[] = [];
      let hasGeometrie = false;
      let hasObjets = false;

      if (backgroundColor === "white") {
        couleurExclude.push("blanc");
      } else if (backgroundColor === "grey") {
        couleurExclude.push("gris");
      } else if (backgroundColor === "black") {
        couleurExclude.push("noir");
      }

      type_forme.forEach((forme) => {
        switch (forme) {
          case "géométrique":
            types.push("geometrie");
            // Pour géométrie, utiliser plein ou vide selon les couleurs
            geometrieSpecificite.push("plein", "vide"); // Blanc peut être plein ou vide
            hasGeometrie = true;
            break;
          case "barres":
            types.push("geometrie");
            geometrieSpecificite.push("horizontal", "vertical", "diagonal1", "diagonal2"); // Barres = horizontal/vertical/diagonal
            hasGeometrie = true;
            break;
          case "objets":
            types.push("objet");
            hasObjets = true;
            break;
            default:
              console.warn(`Forme non reconnue: ${forme}`);
              break;
            }
          });

      if (hasGeometrie && couleurs === "white" && !couleurInclude.includes("blanc")) {
        couleurInclude.push("blanc");
      }

      if (hasObjets) {
        if (couleurs === "white" && !couleurInclude.includes("nb")) {
          couleurInclude.push("nb");
        }
        if (couleurs === "colored" && !couleurInclude.includes("couleur")) {
          couleurInclude.push("couleur");
        }
      }

      if (!mouvement) {
        objetsSpecificite.push("fixe");
      } else {
        objetsSpecificite.push("fixe", "mouvement");
      }

      const params: ImageSelectionParams = {
        type: types.length > 0 ? types : ["objet"],
        geometrieSpecificite:
          geometrieSpecificite.length > 0 ? geometrieSpecificite : undefined,
        objetsSpecificite:
          objetsSpecificite.length > 0 ? objetsSpecificite : ["fixe"],
        couleurInclude: couleurInclude.length > 0 ? couleurInclude : undefined,
        couleurExclude: couleurExclude.length > 0 ? couleurExclude : undefined,
        geometrieFormes:
          geometrieFormes.length > 0 ? geometrieFormes : undefined,
      };
      
      return selectImagesCatalog(params);
    };

    const selectFilesGifsForModulesLocalisation = (
      couleurs: string,
      backgroundColor: string
    ) => {
      const types: ImageType[] = ["geometrie"];
      const geometrieSpecificite: GeometrieSpecificite[] = ["plein"];
      const couleurInclude: ColorImages[] = [];
      const couleurExclude: ColorImages[] = [];
      const geometrieFormes: GeometrieFormes[] = ["rond"];

      // Mapping précis des couleurs pour la localisation (rond)
      if (couleurs === "white") {
        couleurInclude.push("blanc");
      } else if (couleurs === "grey") {
        couleurInclude.push("gris");
      } else if (couleurs === "black") {
        couleurInclude.push("noir");
      }

      if (backgroundColor === "white") {
        couleurExclude.push("blanc");
      } else if (backgroundColor === "grey") {
        couleurExclude.push("gris");
      } else if (backgroundColor === "black") {
        couleurExclude.push("noir");
      }

        const params: ImageSelectionParams = {
          type: types.length > 0 ? types : ["geometrie"],
          geometrieSpecificite:
            geometrieSpecificite.length > 0 ? geometrieSpecificite : undefined,
          couleurInclude,
          couleurExclude:
            couleurExclude.length > 0 ? couleurExclude : undefined,
          geometrieFormes:
            geometrieFormes.length > 0 ? geometrieFormes : undefined,
        };

      return selectImagesCatalog(params);
    };

    const randomImagesCouleurs = selectRandomImages({
      catalog: selectImagesCatalog({
        type: ["geometrie"],
        geometrieSpecificite: ["plein"],
        couleurExclude: ["noir"],
        geometrieFormes: ["rond"],
      }),
    });

    const randomImagesIdentification = selectRandomImages({
      catalog: selectFilesGifsForModulesIdentificationAndFormes(
        sessionInfo.parameters?.ZPI?.couleur_cible ?? "white",
        sessionInfo.parameters?.ZPI?.type_forme ?? ["objets"],
        sessionInfo.parameters?.ZPI?.couleur_fond ?? "black",
        sessionInfo.parameters?.ZPI?.mouvement === "true"
      ),
    });

    const randomImagesLocalisation = selectRandomImages({
      catalog: selectFilesGifsForModulesLocalisation(
        sessionInfo.parameters?.ZPL?.couleur_cible ?? "white",
        sessionInfo.parameters?.ZPL?.couleur_fond ?? "black"
      ),
    });

    const randomImagesFormes = selectRandomImages({
      catalog: selectFilesGifsForModulesIdentificationAndFormes(
        sessionInfo.parameters?.ZPFCF?.couleur_cible ?? "white",
        sessionInfo.parameters?.ZPFCF?.type_forme ?? ["objets"],
        sessionInfo.parameters?.ZPFCF?.couleur_fond ?? "black",
        sessionInfo.parameters?.ZPFCF?.mouvement === "true"
      ),
    });

    console.log("Random images selected for modules.");
    console.log("ZPI Random Images:", randomImagesIdentification);
    console.log("ZPFCF Random Images:", randomImagesFormes);
    const zpiSizes = convertSizesToNumbers(
      sessionInfo.parameters?.ZPI?.taille || []
    );
    const zplSizes = convertSizesToNumbers(
      sessionInfo.parameters?.ZPL?.taille || []
    );
    const zpfcfSizes = convertSizesToNumbers(
      sessionInfo.parameters?.ZPFCF?.taille || []
    );
    const zpccfSizes = convertSizesToNumbers(
      sessionInfo.parameters?.ZPCCF?.taille || []
    );

    const caseSize = Number(localStorage.getItem("selectedCaseSizePx"));

    const maxCanvasWidth = window.innerWidth;
    const maxCanvasHeight = window.innerHeight;

    const { visibleColumns, visibleRows, removedColumns, removedRows } =
      computeVisibleGrid(caseSize, maxCanvasWidth, maxCanvasHeight);

    setRemovedColumns(removedColumns);
    setRemovedRows(removedRows);

    setTargetChampsProfondIdentification(
      generateChampProfondIdentificationTargets(
        casesSelectedZPI,
        400,
        nombreParHemichamp(sessionInfo.parameters?.ZPI?.nombre_par_hemichamp),
        randomImagesIdentification,
        zpiSizes || [],
        visibleColumns,
        visibleRows
      )
    );
    setTargetChampsProfondLocalisation(
      generateChampProfondIdentificationTargets(
        casesSelectedZPL,
        400,
        nombreParHemichamp(sessionInfo.parameters?.ZPL?.nombre_par_hemichamp),
        randomImagesLocalisation,
        zplSizes || [],
        visibleColumns,
        visibleRows
      )
    );
    setTargetsChampProfondFormesTargets(
      generateChampProfondFormesTargets(
        casesSelectedZPFCF,
        randomImagesFormes,
        {
          blinkingAndResizing:
            sessionInfo.parameters?.ZPFCF?.mouvement === "true",
          sizes: zpfcfSizes || [],
        },
        "module_formes"
      )
    );
    setTargetChampsProfondCouleurFeedback(
      generateChampProfondFormesTargets(
        casesSelectedZPCCF,
        randomImagesCouleurs,
        {
          blinkingAndResizing:
            sessionInfo.parameters?.ZPCCF?.mouvement === "true",
          sizes: zpccfSizes || [],
        },
        "module_couleurs"
      )
    );

    setTargetZoneFrontiere(
      generateZoneFrontiereTargets(casesSelectedGeneral, {
        nombreCiblesParCases: parseInt(
          sessionInfo.parameters?.ZF?.nombre_cible ?? "5",
          10
        ),
      })
    );

    console.log("Targets generated for all modules.");
    console.log("ZPI Targets:", targetChampsProfondIdentification);
    console.log("ZPFCF Targets:", targetsChampProfondFormesTargets);
  }, [
    sessionInfo,
    casesSelectedGeneral,
    casesSelectedZPCCF,
    casesSelectedZPFCF,
    casesSelectedZPI,
    casesSelectedZPL,
  ]);

  const startSession = () => {
    const caseSizeName =
      localStorage.getItem("selectedCaseSizeName") || "Default";
    if (!sessionId) return;
    const fieldsToUpdate = [
      { field: "status", value: "EN_COURS" },
      {
        field: "visualFieldInput",
        value: JSON.stringify(casesSelectedRef.current),
      },
      { field: "removedRows", value: removedRows },
      { field: "removedColumns", value: removedColumns },
      { field: "screenUsed", value: String(caseSizeName) },
      {
        field: "accountUsed",
        value: role === "practitioner" ? "CLINIQUE" : "PATIENT",
      },
    ];
    try {
      saveResultsModules(fieldsToUpdate, sessionId, role).catch((error) => {
        console.error(
          "An error occurred while updating session status:",
          error
        );
      });
    } catch (error) {
      console.error(
        "An error occurred while preparing session status update:",
        error
      );
    }
  };

  return (
    <ModuleRenderer
      sessionInfo={sessionInfo!}
      // userContext={userContext!}
      targetZoneFrontiere={targetZoneFrontiere}
      targetChampsProfondIdentification={targetChampsProfondIdentification}
      targetChampsProfondCouleurFeedback={targetChampsProfondCouleurFeedback}
      targetsChampProfondFormesTargets={targetsChampProfondFormesTargets}
      targetChampsProfondLocalisation={targetChampsProfondLocalisation}
      casesSelected={casesSelectedGeneral}
      casesSelectedRef={casesSelectedRef}
      sessionId={sessionId!}
      role={role}
      startSession={startSession}
    />
  );
}
